import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, Button, Modal, Form, Spinner } from 'react-bootstrap';
import ApiService from '../../helpers/apiService';

interface OtpRoutingRule {
  id: string;
  phone_prefix: string;
  priority_channel: string;
  fallback_channel: string;
  is_active: boolean;
}

const CHANNEL_OPTIONS = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'sms', label: 'SMS' }
];

const FALLBACK_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'none', label: 'Нет' }
];

const channelBadge = (channel: string) => {
  if (channel === 'telegram') return <Badge bg="info">Telegram</Badge>;
  if (channel === 'sms') return <Badge bg="warning" text="dark">SMS</Badge>;
  if (channel === 'none') return <Badge bg="secondary">Нет</Badge>;
  return <Badge bg="secondary">{channel}</Badge>;
};

const OtpRoutingPage: React.FC = () => {
  const [rules, setRules] = useState<OtpRoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<OtpRoutingRule | null>(null);
  const [saving, setSaving] = useState(false);

  const [formPhonePrefix, setFormPhonePrefix] = useState('');
  const [formPriorityChannel, setFormPriorityChannel] = useState('telegram');
  const [formFallbackChannel, setFormFallbackChannel] = useState('sms');
  const [formIsActive, setFormIsActive] = useState(true);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ApiService.getOtpRouting<OtpRoutingRule[]>();
      setRules(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load OTP routing rules:', e);
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const openCreateModal = () => {
    setEditingRule(null);
    setFormPhonePrefix('');
    setFormPriorityChannel('telegram');
    setFormFallbackChannel('sms');
    setFormIsActive(true);
    setShowModal(true);
  };

  const openEditModal = (rule: OtpRoutingRule) => {
    setEditingRule(rule);
    setFormPhonePrefix(rule.phone_prefix);
    setFormPriorityChannel(rule.priority_channel);
    setFormFallbackChannel(rule.fallback_channel);
    setFormIsActive(rule.is_active);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formPhonePrefix.trim()) return;
    setSaving(true);
    try {
      const payload = {
        phone_prefix: formPhonePrefix.trim(),
        priority_channel: formPriorityChannel,
        fallback_channel: formFallbackChannel,
        is_active: formIsActive
      };
      if (editingRule) {
        await ApiService.updateOtpRouting(editingRule.id, payload);
      } else {
        await ApiService.createOtpRouting(payload);
      }
      setShowModal(false);
      await loadRules();
    } catch (e) {
      console.error('Failed to save OTP routing rule:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule: OtpRoutingRule) => {
    if (!window.confirm(`Удалить правило для префикса "${rule.phone_prefix}"?`)) return;
    try {
      await ApiService.deleteOtpRouting(rule.id);
      await loadRules();
    } catch (e) {
      console.error('Failed to delete OTP routing rule:', e);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">
                OTP-каналы{' '}
                <Badge bg="secondary" className="ms-2">{rules.length}</Badge>
              </h5>
              <p className="text-muted mb-0 small">
                Управление маршрутизацией OTP-кодов по каналам доставки (Telegram / SMS)
              </p>
            </div>
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              Добавить правило
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center text-muted py-4">
              Нет правил маршрутизации
            </div>
          ) : (
            <Table hover responsive size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Префикс</th>
                  <th>Приоритетный канал</th>
                  <th>Fallback</th>
                  <th>Активен</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(rule => (
                  <tr key={rule.id}>
                    <td><code>{rule.phone_prefix}</code></td>
                    <td>{channelBadge(rule.priority_channel)}</td>
                    <td>{channelBadge(rule.fallback_channel)}</td>
                    <td>
                      <Badge bg={rule.is_active ? 'success' : 'secondary'}>
                        {rule.is_active ? 'Да' : 'Нет'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openEditModal(rule)}
                        >
                          Ред.
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(rule)}
                        >
                          Del
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {editingRule ? 'Редактировать правило' : 'Новое правило'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="small">Префикс телефона</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="+7, +380, +49..."
                value={formPhonePrefix}
                onChange={e => setFormPhonePrefix(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small">Приоритетный канал</Form.Label>
              <Form.Select
                size="sm"
                value={formPriorityChannel}
                onChange={e => setFormPriorityChannel(e.target.value)}
              >
                {CHANNEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small">Fallback канал</Form.Label>
              <Form.Select
                size="sm"
                value={formFallbackChannel}
                onChange={e => setFormFallbackChannel(e.target.value)}
              >
                {FALLBACK_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Активно"
              checked={formIsActive}
              onChange={e => setFormIsActive(e.target.checked)}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving || !formPhonePrefix.trim()}
          >
            {saving ? <Spinner animation="border" size="sm" /> : (editingRule ? 'Сохранить' : 'Создать')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OtpRoutingPage;
